/**
 * PageRenderer — Renders a single PDF page image with optional text overlay
 * and chunk highlight overlays.
 *
 * The component uses a relative-positioned container with:
 * 1. The page image (scaled to fit container width)
 * 2. Invisible text spans overlay (for browser text selection / search)
 * 3. Chunk highlight rectangles (absolute-positioned by bbox percentage)
 *
 * Text spans are lazy-loaded when the page becomes visible via an
 * IntersectionObserver callback from the parent.
 */
import React, { useCallback, useRef, useEffect } from 'react';
import type {
  PageInfo, TextSpan, ChunkMetadata, TableRegion, LineRegion, ChunkBbox,
} from '../../types/viewer';
import { regionSourceText } from '../../types/viewer';
import ChunkHighlight from './ChunkHighlight';
import { computeAnchorRects, keepDistinctRect } from './anchorBbox';
import type { ChunkAnchor } from './RegulatoryViewerEmbed';
import { buildColoredMatchers, withTermMarks, type TermMatcher } from './termMarks';
import { isCitationBboxHighlightEnabled } from '../../config/featureFlags';

// Re-export so existing importers (`import { TermMatcher } from './PageRenderer'`)
// keep working; the implementation now lives in the shared ./termMarks util.
export type { TermMatcher };

/**
 * ScaledSpan — renders a single text span with scaleX to match the PDF bbox width.
 * Measures the natural rendered width, then applies transform: scaleX(targetWidth/naturalWidth).
 * This is the same technique PDF.js uses for its text layer.
 *
 * When ``termRegex`` is supplied, matched word(s) inside the line are wrapped
 * in a <mark> so just the word is highlighted (the span aligns with the page
 * image, so the mark's background lands exactly behind the word).
 */
function ScaledSpan({ span, matchers }: { span: TextSpan; matchers: TermMatcher[] }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset scale to measure natural width
    el.style.transform = 'scaleX(1)';
    const natural = el.getBoundingClientRect().width;
    const container = el.parentElement?.getBoundingClientRect().width;
    if (!natural || !container) return;
    const targetWidth = (span.x1 - span.x0) * container;
    if (natural > 0 && targetWidth > 0) {
      el.style.transform = `scaleX(${targetWidth / natural})`;
    }
  }, [span]);

  return (
    <span
      ref={ref}
      className="absolute"
      style={{
        left: `${span.x0 * 100}%`,
        top: `${span.y0 * 100}%`,
        height: `${(span.y1 - span.y0) * 100}%`,
        color: 'transparent',
        fontSize: `${(span.fontSize || (span.y1 - span.y0)) * 100}cqh`,
        lineHeight: '1',
        whiteSpace: 'nowrap',
        transformOrigin: 'top left',
        userSelect: 'text',
        cursor: 'text',
      }}
    >
      {withTermMarks(span.text, matchers)}
    </span>
  );
}

/**
 * BlockHoverLayer — JP-only. Groups this page's text spans by their
 * PyMuPDF ``block`` id into paragraph-level regions. On hover the source
 * block is highlighted and *everything else dims* (box-shadow mask), and
 * the block's English is pushed to a side panel via ``onHover`` — the
 * translation never covers the page (the source image stays the truth).
 * Translations are lazy + pre-warmed sequentially, cached server-side.
 *
 * Gated entirely behind the ``blockHoverTranslate`` prop on PageRenderer,
 * so non-JP families render none of this and keep their text-selection.
 */
export interface JpHoverRegion {
  ja: string;
  en: string | undefined;
  kind: 'block' | 'table' | 'region';
  isHtml?: boolean; // table translations are <table> HTML
  // Geometry of the hovered region, so the viewer can resolve it to the
  // section it sits in and drive a section-precise TOC highlight (the
  // page-based scroll-spy can't disambiguate two sections on one page).
  // ``page`` is injected by PageRenderer (the layers don't know it);
  // ``y0`` is the hovered box's normalized (0-1) top, set by each layer.
  page?: number | undefined;
  y0?: number | undefined;
}
interface BlockGroup { block: number; x0: number; y0: number; x1: number; y1: number; ja: string }

/**
 * AutoSelect — a programmatic "select this block" signal from a clicked search
 * result (bilingual jp_pdf / zh_pdf only). Instead of manually hovering to find
 * where the searched word sits on the page, clicking a result drives the hover
 * layer to pick the matching block, highlight it, translate it into the panel,
 * and scroll it into view — the same path a hover takes, fired from a click.
 *
 *  - ``page``  — 1-based page the result landed on (the layer ignores it unless
 *                it matches its own page; PageRenderer already gates on this).
 *  - ``term``  — the searched query (search-click path).
 *  - ``mode``  — 'src' when the query is CJK (match the block's source text 1:1)
 *                or 'en' when the query is English (match the block's live
 *                English translation, which fills in lazily as it resolves).
 *  - ``bbox``  — citation-land path: the cited chunk's normalized [x0,y0,x1,y1]
 *                band. The region with the largest y-overlap is auto-selected +
 *                translated, so a CEPI citation lands on the clean paragraph box
 *                (no hovering). Geometry is available immediately, so unlike the
 *                lazy 'en' term match this fires as soon as the layer mounts.
 *  - ``nonce`` — bumped on every trigger so re-firing the same one re-selects.
 *
 * Exactly one of ``term`` / ``bbox`` is set per signal.
 */
export interface AutoSelect {
  page: number;
  nonce: number;
  term?: string;
  mode?: 'en' | 'src';
  bbox?: [number, number, number, number];
}

function groupSpansByBlock(spans: TextSpan[]): BlockGroup[] {
  const m = new Map<number, BlockGroup>();
  for (const s of spans) {
    const b = s.block;
    if (b === undefined || b < 0) continue; // -1 = OCR/unknown span, not a real block
    const g = m.get(b);
    if (!g) {
      m.set(b, { block: b, x0: s.x0, y0: s.y0, x1: s.x1, y1: s.y1, ja: s.text });
    } else {
      g.x0 = Math.min(g.x0, s.x0); g.y0 = Math.min(g.y0, s.y0);
      g.x1 = Math.max(g.x1, s.x1); g.y1 = Math.max(g.y1, s.y1);
      g.ja += ' ' + s.text;
    }
  }
  return [...m.values()];
}

// A block whose center falls inside any table region is owned by that
// table (the table layer handles it), so block hover skips it.
function centerInAnyTable(g: BlockGroup, tables: TableRegion[]): boolean {
  const cx = (g.x0 + g.x1) / 2, cy = (g.y0 + g.y1) / 2;
  return tables.some(
    (t) => cx >= t.bbox[0] && cx <= t.bbox[2] && cy >= t.bbox[1] && cy <= t.bbox[3],
  );
}

function BlockHoverLayer({
  spans,
  tables,
  translate,
  onHover,
  autoSelect,
  onAutoSelected,
}: {
  spans: TextSpan[];
  tables: TableRegion[];
  translate: (ja: string, preserveHtml?: boolean, y0?: number) => Promise<string>;
  onHover: (r: JpHoverRegion | null) => void;
  autoSelect?: AutoSelect | undefined;
  onAutoSelected?: (() => void) | undefined;
}) {
  const groups = React.useMemo(
    () => groupSpansByBlock(spans).filter((g) => !centerInAnyTable(g, tables)),
    [spans, tables],
  );
  const [en, setEn] = React.useState<Record<number, string>>({});
  const [hovered, setHovered] = React.useState<number | null>(null);
  const pending = useRef<Set<number>>(new Set());
  const elRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const ensure = useCallback(
    async (g: BlockGroup) => {
      if (en[g.block] !== undefined || pending.current.has(g.block)) return;
      pending.current.add(g.block);
      try {
        const t = await translate(g.ja, false, g.y0);
        setEn((prev) => ({ ...prev, [g.block]: t }));
      } catch {
        pending.current.delete(g.block); // allow retry on next hover
      }
    },
    [en, translate],
  );

  // Search-click auto-select: pick the block whose source (CJK query) or live
  // English (English query) contains the term, activate it exactly as a hover
  // would (highlight + translate + push to panel), and scroll it into view. In
  // 'en' mode the translation may still be resolving, so the effect re-runs as
  // ``en`` fills in and fires once a match appears; ``nonce`` de-dupes so it
  // activates only once per click.
  const autoRef = useRef<number | null>(null);
  useEffect(() => {
    if (!autoSelect || autoRef.current === autoSelect.nonce) return;
    let target: BlockGroup | undefined;
    if (autoSelect.bbox) {
      // citation-land: the block with the largest y-overlap with the cited band.
      const [, by0, , by1] = autoSelect.bbox;
      let best = 0;
      for (const g of groups) {
        const ov = Math.max(0, Math.min(g.y1, by1) - Math.max(g.y0, by0));
        if (ov > best) { best = ov; target = g; }
      }
    } else if (autoSelect.term) {
      const term = autoSelect.term.trim();
      if (!term) return;
      const lc = term.toLowerCase();
      for (const g of groups) {
        const hit =
          autoSelect.mode === 'src'
            ? g.ja.includes(term)
            : (en[g.block]?.toLowerCase().includes(lc) ?? false);
        if (hit) { target = g; break; }
      }
    }
    if (!target) return; // no match yet ('en' term mode waits for more translations)
    autoRef.current = autoSelect.nonce;
    setHovered(target.block);
    ensure(target);
    elRefs.current.get(target.block)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    onAutoSelected?.(); // consumed → parent clears the signal so a remount can't re-fire
  }, [autoSelect, en, groups, ensure, onAutoSelected]);

  // Pre-warm SEQUENTIALLY (gentle on the translate rate limit) so a hover
  // is instant; the per-page lazy render keeps this scoped to visible pages.
  useEffect(() => {
    let alive = true;
    (async () => {
      for (const g of groups) {
        if (!alive) return;
        await ensure(g);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Push the hovered block (and its translation as it arrives) to the
  // side panel. Cleared on un-hover.
  const hoveredGroup = hovered != null ? groups.find((g) => g.block === hovered) : undefined;
  const hoveredJa = hoveredGroup?.ja;
  const hoveredY0 = hoveredGroup?.y0;
  useEffect(() => {
    if (hovered == null || hoveredJa === undefined) { onHover(null); return; }
    onHover({ ja: hoveredJa, en: en[hovered], kind: 'block', y0: hoveredY0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered, hoveredJa, hoveredY0, en]);

  return (
    // pointerEvents none on the container so its empty areas don't steal
    // hover from lower layers; each region div re-enables pointer events.
    <div className="absolute inset-0" style={{ zIndex: 15, pointerEvents: 'none' }}>
      {groups.map((g) => {
        const isH = hovered === g.block;
        return (
          <div
            key={g.block}
            ref={(el) => { if (el) elRefs.current.set(g.block, el); else elRefs.current.delete(g.block); }}
            onMouseEnter={() => { setHovered(g.block); ensure(g); }}
            onMouseLeave={() => setHovered((c) => (c === g.block ? null : c))}
            className="absolute rounded-sm"
            style={{
              left: `${g.x0 * 100}%`,
              top: `${g.y0 * 100}%`,
              width: `${(g.x1 - g.x0) * 100}%`,
              height: `${(g.y1 - g.y0) * 100}%`,
              cursor: 'help',
              pointerEvents: 'auto',
              background: isH ? 'rgba(99,102,241,0.10)' : 'transparent',
              // Dim EVERYTHING ELSE so it's unmistakable what got translated
              // (a giant outset shadow darkens the rest of the page).
              boxShadow: isH
                ? '0 0 0 2px rgba(99,102,241,0.9), 0 0 0 9999px rgba(15,23,42,0.34)'
                : 'none',
              zIndex: isH ? 1 : 0,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * TableHoverLayer — JP-only. One hover region per detected table
 * (find_tables, stored at ingest). Hovering shows the WHOLE table's
 * translated grid in the side panel — never a single flattened cell.
 * Green to distinguish from indigo block regions; translated with
 * preserve_html so the <table> structure survives.
 */
function TableHoverLayer({
  tables,
  translate,
  onHover,
}: {
  tables: TableRegion[];
  translate: (ja: string, preserveHtml?: boolean, y0?: number) => Promise<string>;
  onHover: (r: JpHoverRegion | null) => void;
}) {
  const [en, setEn] = React.useState<Record<number, string>>({});
  const [hovered, setHovered] = React.useState<number | null>(null);
  const pending = useRef<Set<number>>(new Set());

  const ensure = useCallback(
    async (i: number) => {
      const tbl = tables[i];
      if (!tbl || en[i] !== undefined || pending.current.has(i)) return;
      pending.current.add(i);
      try {
        const t = await translate(tbl.html_ja, true, tbl.bbox[1]);
        setEn((prev) => ({ ...prev, [i]: t }));
      } catch {
        pending.current.delete(i);
      }
    },
    [en, translate, tables],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      for (let i = 0; i < tables.length; i++) {
        if (!alive) return;
        await ensure(i);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  const hJa = hovered != null ? tables[hovered]?.html_ja : undefined;
  const hY0 = hovered != null ? tables[hovered]?.bbox[1] : undefined;
  useEffect(() => {
    if (hovered == null || hJa === undefined) { onHover(null); return; }
    onHover({ ja: hJa, en: en[hovered], kind: 'table', isHtml: true, y0: hY0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered, hJa, hY0, en]);

  return (
    <div className="absolute inset-0" style={{ zIndex: 16, pointerEvents: 'none' }}>
      {tables.map((t, i) => {
        const isH = hovered === i;
        return (
          <div
            key={i}
            onMouseEnter={() => { setHovered(i); ensure(i); }}
            onMouseLeave={() => setHovered((c) => (c === i ? null : c))}
            className="absolute rounded-sm"
            style={{
              left: `${t.bbox[0] * 100}%`,
              top: `${t.bbox[1] * 100}%`,
              width: `${(t.bbox[2] - t.bbox[0]) * 100}%`,
              height: `${(t.bbox[3] - t.bbox[1]) * 100}%`,
              cursor: 'help',
              pointerEvents: 'auto',
              background: isH ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.04)',
              boxShadow: isH
                ? '0 0 0 2px rgba(16,185,129,0.95), 0 0 0 9999px rgba(15,23,42,0.34)'
                : 'none',
              zIndex: isH ? 1 : 0,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * RegionHoverLayer — JP leaflet only. One hover region per LOGICAL unit
 * (header = own box; wrapped bullet / body paragraph = one box), computed at
 * ingest from the leaflet grammar (line_regions). Replaces the raw-PyMuPDF
 * BlockHoverLayer when line_regions are present, so boxes align to logical
 * units instead of layout blocks. Headers/subheadings carry a persistent
 * subtle tint so they read as headings; the on-hover scrim is light (0.12 vs
 * the block layer's 0.34) because line-level boxes make a heavy dim jarring.
 */
const REGION_STYLE: Record<LineRegion['region_type'], { border: string; bg: string; hoverBg: string }> = {
  heading:    { border: 'rgba(79,70,229,0.95)', bg: 'rgba(79,70,229,0.10)',  hoverBg: 'rgba(79,70,229,0.18)' },
  subheading: { border: 'rgba(99,102,241,0.85)', bg: 'rgba(99,102,241,0.06)', hoverBg: 'rgba(99,102,241,0.14)' },
  bullet:     { border: 'rgba(99,102,241,0.75)', bg: 'transparent',           hoverBg: 'rgba(99,102,241,0.10)' },
  body:       { border: 'rgba(99,102,241,0.75)', bg: 'transparent',           hoverBg: 'rgba(99,102,241,0.10)' },
};

/**
 * A line-region is hidden only when it is MOSTLY inside a table (the table
 * layer owns those cells). Center-point containment was too aggressive: a tall
 * subsection box (heading + its body text just above a table) whose midpoint
 * happens to fall inside an oversized find_tables bbox got hidden entirely,
 * making the text above/below a table un-hoverable (looked like dropped data).
 * Area-overlap keeps such boxes visible — only genuine in-table content (the
 * bulk of its area within the grid) is suppressed.
 */
function regionMostlyInTable(r: LineRegion, tables: TableRegion[]): boolean {
  const [rx0, ry0, rx1, ry1] = r.bbox;
  const ra = Math.max(0, rx1 - rx0) * Math.max(0, ry1 - ry0);
  if (ra <= 0) return false;
  for (const t of tables) {
    const ix = Math.max(0, Math.min(rx1, t.bbox[2]) - Math.max(rx0, t.bbox[0]));
    const iy = Math.max(0, Math.min(ry1, t.bbox[3]) - Math.max(ry0, t.bbox[1]));
    // 0.8, not a center-point: line-regions already exclude table-cell lines at
    // ingest, so a surviving region is real text — only hide one that is almost
    // wholly inside an (often oversized) find_tables bbox.
    if ((ix * iy) / ra > 0.8) return true;
  }
  return false;
}

function RegionHoverLayer({
  regions,
  tables,
  translate,
  onHover,
  autoSelect,
  onAutoSelected,
}: {
  regions: LineRegion[];
  tables: TableRegion[];
  translate: (ja: string, preserveHtml?: boolean, y0?: number) => Promise<string>;
  onHover: (r: JpHoverRegion | null) => void;
  autoSelect?: AutoSelect | undefined;
  onAutoSelected?: (() => void) | undefined;
}) {
  const visible = React.useMemo(
    () => regions.filter((r) => !regionMostlyInTable(r, tables)),
    [regions, tables],
  );
  const [en, setEn] = React.useState<Record<number, string>>({});
  const [hovered, setHovered] = React.useState<number | null>(null);
  const pending = useRef<Set<number>>(new Set());
  const elRefs = useRef<(HTMLDivElement | null)[]>([]);

  const ensure = useCallback(
    async (i: number) => {
      const r = visible[i];
      if (!r || en[i] !== undefined || pending.current.has(i)) return;
      pending.current.add(i);
      try {
        // JP line_regions carry the source in text_ja; zh_pdf uses text_zh; ch_pdf text_src.
        const t = await translate(regionSourceText(r), false, r.bbox[1]);
        setEn((prev) => ({ ...prev, [i]: t }));
      } catch {
        pending.current.delete(i);
      }
    },
    [en, translate, visible],
  );

  // Search-click auto-select: pick the region whose source (CJK query) or live
  // English (English query) contains the term, activate it exactly as a hover
  // would (highlight + translate + push to panel), and scroll it into view. In
  // 'en' mode the translation may still be resolving, so the effect re-runs as
  // ``en`` fills in and fires once a match appears; ``nonce`` de-dupes so it
  // activates only once per click.
  const autoRef = useRef<number | null>(null);
  useEffect(() => {
    if (!autoSelect || autoRef.current === autoSelect.nonce) return;
    let idx = -1;
    if (autoSelect.bbox) {
      // citation-land: the region with the largest y-overlap with the cited
      // chunk's band. Geometry is available now, so this resolves on mount.
      const [, by0, , by1] = autoSelect.bbox;
      let best = 0;
      for (let i = 0; i < visible.length; i++) {
        const rb = visible[i]?.bbox;
        if (!rb) continue;
        const ov = Math.max(0, Math.min(rb[3], by1) - Math.max(rb[1], by0));
        if (ov > best) { best = ov; idx = i; }
      }
    } else if (autoSelect.term) {
      const term = autoSelect.term.trim();
      if (!term) return;
      const lc = term.toLowerCase();
      for (let i = 0; i < visible.length; i++) {
        const vr = visible[i];
        const src = vr ? regionSourceText(vr) : '';
        const hit =
          autoSelect.mode === 'src'
            ? src.includes(term)
            : (en[i]?.toLowerCase().includes(lc) ?? false);
        if (hit) { idx = i; break; }
      }
    }
    if (idx < 0) return; // no match yet ('en' term mode waits for more translations)
    autoRef.current = autoSelect.nonce;
    setHovered(idx);
    ensure(idx);
    elRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    onAutoSelected?.(); // consumed → parent clears the signal so a remount can't re-fire
  }, [autoSelect, en, visible, ensure, onAutoSelected]);

  useEffect(() => {
    let alive = true;
    (async () => {
      for (let i = 0; i < visible.length; i++) {
        if (!alive) return;
        await ensure(i);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const hoveredRegion = hovered != null ? visible[hovered] : undefined;
  const hJa = hoveredRegion ? regionSourceText(hoveredRegion) : undefined;
  const hY0 = hovered != null ? visible[hovered]?.bbox[1] : undefined;
  useEffect(() => {
    if (hovered == null || hJa === undefined) { onHover(null); return; }
    onHover({ ja: hJa, en: en[hovered], kind: 'region', y0: hY0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered, hJa, hY0, en]);

  return (
    <div className="absolute inset-0" style={{ zIndex: 15, pointerEvents: 'none' }}>
      {visible.map((r, i) => {
        const isH = hovered === i;
        const st = REGION_STYLE[r.region_type];
        return (
          <div
            key={i}
            ref={(el) => { elRefs.current[i] = el; }}
            onMouseEnter={() => { setHovered(i); ensure(i); }}
            onMouseLeave={() => setHovered((c) => (c === i ? null : c))}
            className="absolute rounded-sm"
            style={{
              left: `${r.bbox[0] * 100}%`,
              top: `${r.bbox[1] * 100}%`,
              width: `${(r.bbox[2] - r.bbox[0]) * 100}%`,
              height: `${(r.bbox[3] - r.bbox[1]) * 100}%`,
              cursor: 'help',
              pointerEvents: 'auto',
              background: isH ? st.hoverBg : st.bg,
              // Just outline the hovered box — no full-page dim scrim (the
              // constant dimming on every hover was distracting).
              boxShadow: isH ? `0 0 0 2px ${st.border}` : 'none',
              zIndex: isH ? 1 : 0,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * RegionSearchHighlight — JP scanned pages only. On a scanned page the
 * text_spans are garbage Tesseract output, so the word-level search highlight
 * (which paints into the text-span overlay) can't match anything. But the
 * box-OCR ``line_regions`` carry the REAL Japanese text (``text_ja``) with
 * bboxes — the same layer hover uses. So for a Japanese search we box every
 * region whose text_ja matches the query. Coarser than word-level (whole
 * line/block), but it's the only reliable Japanese geometry on scanned pages.
 */
function RegionSearchHighlight({
  regions,
  matchers,
}: {
  regions: LineRegion[];
  matchers: TermMatcher[];
}) {
  if (!matchers.length) return null;
  const hits = regions.filter((r) => {
    const t = regionSourceText(r);
    return matchers.some((m) => {
      m.re.lastIndex = 0;
      return m.re.test(t);
    });
  });
  if (!hits.length) return null;
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 15 }}>
      {hits.map((r, i) => {
        const [x0, y0, x1, y1] = r.bbox;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: `${x0 * 100}%`,
              top: `${y0 * 100}%`,
              width: `${(x1 - x0) * 100}%`,
              height: `${(y1 - y0) * 100}%`,
              background: 'rgba(250, 204, 21, 0.42)',
              boxShadow: '0 0 0 1px rgba(202, 138, 4, 0.7)',
              borderRadius: 2,
            }}
          />
        );
      })}
    </div>
  );
}

interface PageRendererProps {
  page: PageInfo;
  pageNum: number;
  /** Text spans for this page (may be undefined if not yet loaded) */
  textSpans: TextSpan[] | undefined;
  /** Chunks to highlight on this page */
  chunks: ChunkMetadata[];
  /**
   * Optional per-chunk anchor phrases.  When a chunk has an entry AND
   * its anchor matches one of this page's ``textSpans``, the highlight
   * is drawn at sentence/line granularity instead of paragraph
   * granularity (the chunk.bbox fallback).  Anchors come from CIPI
   * citations.
   */
  chunkAnchors?: Record<number, ChunkAnchor[]> | undefined;
  /**
   * The cited sentence to box on THIS page, addressed by page rather than by
   * chunk.
   *
   * A progressive-read citation knows its page exactly (the reader runs in
   * page space and refuses a seed whose owning page it cannot verify — see
   * ``read_file.py`` ``source_position_unavailable``) but owns no chunk, so it
   * has no entry in ``chunkAnchors`` and the chunk loop below can never reach
   * it. That is the only thing that was missing: the box itself is computed
   * from the sentence against this page's own ``textSpans``, exactly as the
   * chunk path's tight box is, and ``chunk.bbox`` plays no part in either.
   *
   * Same refusal as everywhere else in this file: anchor not located on this
   * page ⇒ no box. Never a fallback rectangle.
   *
   * A LIST, not one anchor. This was `ChunkAnchor` singular and the map feeding
   * it was `Record<number, ChunkAnchor>` — one anchor per PAGE — so a citation
   * with two cited sentences on one page drew the last one and the reader got
   * no sign the first existed. The server merges only units that TOUCH
   * (`_MERGE_GAP = 1`), so two passages a paragraph apart stay two spans and
   * both are evidence. Identical defect to the one `chunkAnchors` carried, same
   * fix: a list per key.
   */
  pageAnchors?: ChunkAnchor[] | undefined;
  /** Search term to box at word level inside the text layer (Ctrl+F-style). */
  highlightTerm?: string | null | undefined;
  /** Several terms to box (any-of). Takes precedence over highlightTerm — used
   *  by keyword Compare to highlight every query row in the PDF. */
  highlightTerms?: string[] | null | undefined;
  englishExact?: boolean;
  /** Per-term mark colors (parallel to highlightTerms by index). When present,
   *  each query row's match gets its own translucent color in the PDF overlay;
   *  when absent, matches stay amber. */
  highlightColors?: (string | undefined)[] | null | undefined;
  /** Deterministic section-highlight boxes for THIS page (normalized 0..1,
   *  ``[x0,y0,x1,y1]``). Drawn as a translucent overlay — the EU-historical
   *  citation path has no chunk_ids and word-level text matching can't hit a
   *  multi-word section phrase, so the geometry box IS the highlight. */
  sectionBoxes?: [number, number, number, number][] | undefined;
  /** Box of a POSITIVELY MATCHED section heading line on this page, normalized
   *  0-1 [x0,y0,x1,y1]. Blinks on arrival so the start of the section is
   *  findable. Absent when the heading could not be matched — an unmatched
   *  line is never boxed. */
  headingBox?: [number, number, number, number] | undefined;
  /** Bumped per jump so re-jumping to the same heading replays the blink. */
  headingFlashKey?: number | undefined;
  /** The chunk ID that should be focused/active (from URL param) */
  activeChunkId: number | null;
  /** Callback when a chunk highlight is clicked */
  onChunkClick?: (chunk: ChunkMetadata) => void;
  /** Ref callback for IntersectionObserver registration */
  observerRef?: (el: HTMLDivElement | null) => void;
  /** When set, the page shows a "click to zoom" affordance and clicking the
   *  page (outside a chunk highlight) opens the zoom lightbox for this page. */
  onZoomClick?: (pageNum: number) => void;
  /** JP PDF only: translate a block's Japanese text JA→EN. When provided,
   *  native-text pages get the block-hover overlay (and the garbage
   *  Tesseract text layer is suppressed on OCR pages). Other families omit
   *  this and behave exactly as before. */
  blockHoverTranslate?: ((japaneseText: string, preserveHtml?: boolean, page?: number, y0?: number) => Promise<string>) | undefined;
  /** JP PDF only: called as the user hovers a block (and again when its
   *  translation resolves), or with null on un-hover, so a side panel can
   *  display the English instead of a floating tooltip. */
  onBlockHover?: ((region: JpHoverRegion | null) => void) | undefined;
  /** jp_pdf / zh_pdf only: a clicked-search-result signal. When its ``page``
   *  matches this page, the hover layer auto-selects the block containing the
   *  term (highlight + translate + scroll into view) so the user need not hover
   *  to find it. Ignored on other pages / families. */
  autoSelect?: AutoSelect | null | undefined;
  /** Paired with ``autoSelect``: the hover layer calls this once it has
   *  consumed the signal (selected the block) so the parent can clear it —
   *  otherwise a virtualized page's layer, remounting on scroll-back, would
   *  re-fire the same select and yank the scroll. */
  onAutoSelected?: (() => void) | undefined;
  /** Overlay virtualization: when false, the heavy per-page overlays (the
   *  invisible text-selection layer — up to hundreds of <span>s per page —
   *  and the JP hover layers) are NOT mounted, leaving just the lazy <img>.
   *  The parent flips this per page based on viewport proximity so a 400-page
   *  doc keeps ~7 pages' worth of overlay nodes in the DOM instead of ~100k.
   *  Defaults to true so every non-virtualizing viewer behaves exactly as
   *  before. Text-span DATA still lives in the parent's cache (client search
   *  + hover resolution are unaffected); only the DOM rendering is gated. */
  renderOverlays?: boolean;
}

const PageRenderer: React.FC<PageRendererProps> = ({
  page,
  pageNum,
  textSpans,
  chunks,
  chunkAnchors,
  pageAnchors,
  highlightTerm,
  highlightTerms,
  highlightColors,
  englishExact = false,
  sectionBoxes,
  headingBox,
  headingFlashKey,
  activeChunkId,
  onChunkClick,
  observerRef,
  onZoomClick,
  blockHoverTranslate,
  onBlockHover,
  autoSelect,
  onAutoSelected,
  renderOverlays = true,
}) => {
  // Corpus-wide kill switch for the bbox citation highlight (default OFF).
  // Read once per render rather than per chunk — it is a build-time constant.
  const highlightEnabled = isCitationBboxHighlightEnabled();
  // The sentence-level highlight (citation-evidence resolver) is always on and
  // deliberately narrower than the flag above: it draws a box ONLY when the
  // anchor phrase was located in this page's own text_spans. It never licenses
  // the stored chunk rectangle — that box describes ``core_text`` while
  // retrieval matched ``chunk_text``, which is exactly why the flag above is
  // off. A box is shown only over text we actually found.
  // Only the page the clicked result landed on consumes the auto-select; the
  // layers key their one-shot activation off ``nonce``, so gating by page here
  // keeps every other page's layer inert.
  const myAutoSelect = autoSelect && autoSelect.page === pageNum ? autoSelect : undefined;
  // JP hover-translate: enabled on native-text pages only. On scanned
  // (is_ocr) JP pages the text_spans are garbage English (Tesseract has no
  // jpn pack), so we neither hover-translate nor render the text layer.
  const jpHover = !!blockHoverTranslate;
  // Gate hover on USABLE GEOMETRY, not is_ocr. is_ocr is overloaded — it's
  // set true for any page that had ANY OCR'd region (e.g. one embedded table
  // on an otherwise-native page), so it wrongly disables hover on real
  // native pages. The reliable signal: native spans carry block >= 0;
  // Tesseract-garbage spans default to block -1; truly-scanned pages have no
  // spans. So "has any block >= 0 span" == "has real positioned Japanese".
  const hasRealSpans = !!textSpans && textSpans.some((s) => (s.block ?? -1) >= 0);
  const jpBlockHover = jpHover && hasRealSpans;
  const suppressTextLayer = jpHover && !hasRealSpans; // hide garbage/empty overlay
  const handleChunkClick = useCallback(
    (chunk: ChunkMetadata) => {
      onChunkClick?.(chunk);
    },
    [onChunkClick],
  );

  // Tag every hover region with THIS page number before it reaches the
  // parent — the hover layers know the box's y0 but not which page they're
  // on. The viewer uses (page, y0) to resolve the region to its section.
  const handleBlockHover = useCallback(
    (region: JpHoverRegion | null) => {
      onBlockHover?.(region ? { ...region, page: pageNum } : null);
    },
    [onBlockHover, pageNum],
  );

  // Inject THIS page number into the hover translate call (same reason as
  // above: the layers know the box y0, only PageRenderer knows the page). The
  // parent resolves (page, y0) → section_id synchronously for reference-guided
  // translation. undefined when hover is off so the gate below stays correct.
  const blockTranslateWithPage = React.useMemo(
    () =>
      blockHoverTranslate
        ? (ja: string, preserveHtml?: boolean, y0?: number) =>
            blockHoverTranslate(ja, preserveHtml, pageNum, y0)
        : undefined,
    [blockHoverTranslate, pageNum],
  );

  // Per-term matchers (built once per page). When active AND this page has a
  // text layer, the matched WORD is boxed in the text overlay (per-row colored
  // for multi-row keyword queries), so we soften the big chunk rectangle to a
  // faint outline instead.
  const matchers = React.useMemo(
    () =>
      highlightTerms && highlightTerms.length
        ? buildColoredMatchers(highlightTerms, highlightColors, englishExact)
        : buildColoredMatchers(highlightTerm ? [highlightTerm] : null, null, englishExact),
    [highlightTerm, highlightTerms, highlightColors, englishExact],
  );
  const wordMarking = matchers.length > 0 && !!textSpans && textSpans.length > 0;

  // If no image URL, show placeholder
  if (!page.image_url) {
    return (
      <div
        ref={observerRef}
        data-page={pageNum}
        className="relative bg-gray-100 border border-gray-200 rounded mb-4 flex items-center justify-center"
        style={{ minHeight: 400 }}
      >
        <span className="text-gray-400 text-sm">
          Page {pageNum} — image unavailable
        </span>
      </div>
    );
  }

  // Compute natural aspect ratio for responsive sizing
  const aspectRatio =
    page.width && page.height ? page.width / page.height : 8.5 / 11;

  return (
    <div
      ref={observerRef}
      data-page={pageNum}
      className="relative mb-4 shadow-sm border border-gray-200 bg-white"
      style={{ aspectRatio: String(aspectRatio) }}
    >
      {/* Page image. When zoomable, clicking the image (the base layer, below
          the text/chunk overlays) opens the zoom lightbox. Text selection and
          chunk-highlight clicks keep working — they sit above at zIndex 10/20. */}
      <img
        src={page.image_url}
        alt={`Page ${pageNum}`}
        className={`block w-full h-full object-contain${onZoomClick ? ' cursor-zoom-in' : ''}`}
        loading="lazy"
        draggable={false}
        onClick={onZoomClick ? () => onZoomClick(pageNum) : undefined}
      />

      {/* Invisible text overlay for text selection. Suppressed on JP
          scanned pages where text_spans are garbage Tesseract output.
          Also gated by renderOverlays: on a virtualized (large) doc this
          layer — the dominant DOM cost, up to hundreds of <span>s/page —
          is only mounted for pages near the viewport. */}
      {renderOverlays && textSpans && textSpans.length > 0 && !suppressTextLayer && (
        <div
          className="absolute inset-0 select-text"
          style={{ zIndex: 10, containerType: 'size' as never }}
        >
          {textSpans.map((span, idx) => (
            <ScaledSpan key={idx} span={span} matchers={matchers} />
          ))}
        </div>
      )}

      {/* JP PDF hover-translation overlay. line_regions carry their OWN bbox
          geometry (from the box-OCR pass on scanned pages, or the leaflet
          grammar on native pages), so the RegionHoverLayer works INDEPENDENT of
          native text_spans — same as the table layer below. Gating it on
          hasRealSpans wrongly hid hover on scanned pages that DO have real
          line_regions (e.g. interview-form page 1). The BlockHoverLayer (which
          groups raw text_spans) is the fallback only when there are NO
          line_regions, and it still needs real spans (jpBlockHover). */}
      {renderOverlays && jpHover && (page.line_regions?.length ?? 0) > 0 && (
        <RegionHoverLayer
          regions={page.line_regions as LineRegion[]}
          tables={page.table_regions ?? []}
          translate={blockTranslateWithPage!}
          onHover={handleBlockHover}
          autoSelect={myAutoSelect}
          onAutoSelected={onAutoSelected}
        />
      )}

      {/* Japanese search-term highlight on SCANNED pages: text_spans are
          garbage here so word-level highlighting can't fire — match the query
          against the box-OCR line_regions' text_ja and box the whole region.
          Only when the text layer is unusable (suppressTextLayer); native pages
          keep the tighter word-level highlight in the text overlay above. */}
      {renderOverlays && matchers.length > 0 && suppressTextLayer && (page.line_regions?.length ?? 0) > 0 && (
        <RegionSearchHighlight
          regions={page.line_regions as LineRegion[]}
          matchers={matchers}
        />
      )}
      {renderOverlays && jpBlockHover && (page.line_regions?.length ?? 0) === 0 && (
        <BlockHoverLayer
          spans={textSpans as TextSpan[]}
          tables={page.table_regions ?? []}
          translate={blockTranslateWithPage!}
          onHover={handleBlockHover}
          autoSelect={myAutoSelect}
          onAutoSelected={onAutoSelected}
        />
      )}

      {/* JP PDF table-hover overlay (whole-table translated grid). Table
          regions come from find_tables() with real bboxes, so show them
          whenever present — independent of the is_ocr flag. */}
      {renderOverlays && jpHover && (page.table_regions?.length ?? 0) > 0 && (
        <TableHoverLayer
          tables={page.table_regions as TableRegion[]}
          translate={blockTranslateWithPage!}
          onHover={handleBlockHover}
        />
      )}

      {/* Genuinely-scanned page (no real spans, no table regions, AND no
          box-OCR line_regions) → no usable geometry for hover. When line_regions
          ARE present the RegionHoverLayer above handles hover, so don't show the
          "unavailable" banner. */}
      {jpHover && !hasRealSpans && (page.table_regions?.length ?? 0) === 0
        && (page.line_regions?.length ?? 0) === 0 && (
        <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-amber-500/90 text-white text-[11px] px-2 py-0.5 rounded" style={{ zIndex: 30 }}>
          Scanned page — hover translation unavailable
        </div>
      )}

      {/* Chunk highlight overlays.  Suppressed corpus-wide by the
          ``isCitationBboxHighlightEnabled`` kill switch (default OFF) — the
          stored bbox describes ``core_text`` while retrieval matches
          ``chunk_text``, so a match landing in the neighbour overlap boxes the
          wrong text, and on a different page 44.9% of the time.  Scrolling to
          the cited page still happens; only the rectangle is withheld. */}
      {chunks.flatMap((chunk) => {
        // ONE CHUNK OWNS SEVERAL CITED PASSAGES, so this is a list, not a
        // lookup. The model points at the evidence units it was shown
        // (`[E1, E34, E76]`) and the server merges only units that TOUCH
        // (`_MERGE_GAP = 1`), so three units scattered through one chunk stay
        // three independent spans. Keying one anchor per chunk let the last
        // write win and dropped the rest — the reader was shown the final
        // cited passage with no sign the others existed.
        //
        // Sentence-level tightening: intersect each anchor against this page's
        // text_spans and use the tight bbox instead of the chunk's full
        // paragraph bbox.
        const anchors = chunkAnchors?.[chunk.chunk_id] ?? [];
        // ONE RECT PER LINE, carrying which evidence it belongs to.
        //
        // `computeAnchorRects` returns the cited span of each line it covers
        // instead of one rectangle around all of them, so a sentence wrapping
        // three lines paints three strokes that stop where the words stop. The
        // colour index is the ANCHOR's position, not the rect's: the three
        // strokes of one sentence share a colour, and the next sentence gets
        // the next one.
        const tightBboxes: { bbox: ChunkBbox; colorIndex: number }[] = [];
        const keptRects: ChunkBbox[] = [];
        for (const [anchorIdx, anchor] of anchors.entries()) {
          const rects = computeAnchorRects(textSpans, anchor.start, anchor.end, anchor.text);
          if (rects.length === 0) continue;  // per-anchor refusal: costs no sibling
          for (const box of rects) {
            // Dedupe on the RESOLVED rectangle rather than the anchor text, by
            // CONTAINMENT rather than equality — see `keepDistinctRect`.
            const before = keptRects.length;
            if (!keepDistinctRect(keptRects, box)) continue;
            if (keptRects.length === before) {
              // Swallowed a kept rect: replace its entry rather than add one.
              const idx = tightBboxes.findIndex((t) => !keptRects.includes(t.bbox));
              if (idx >= 0) tightBboxes.splice(idx, 1);
            }
            tightBboxes.push({ bbox: box, colorIndex: anchorIdx });
          }
        }
        // ``computeAnchorBbox`` returns already-normalised 0-1 coords
        // (text_spans are normalised on the API side), so when we
        // use it we pass ``pageWidth/pageHeight = 1`` to opt out of
        // ChunkHighlight's PDF-point → percentage conversion.
        // The sentence path may draw ONLY located boxes. Falling back to
        // chunk.bbox here would silently reintroduce the wrong-text rectangle
        // this whole workstream exists to remove — and it would do so under a
        // flag whose name promises sentence precision, which is worse than the
        // original bug because it looks trustworthy.
        const drawn: { bbox: ChunkBbox; usingAnchor: boolean; colorIndex: number }[] =
          tightBboxes.length > 0
            ? tightBboxes.map(({ bbox, colorIndex }) => ({ bbox, usingAnchor: true, colorIndex }))
            : (highlightEnabled && chunk.bbox
              ? [{ bbox: chunk.bbox, usingAnchor: false, colorIndex: 0 }]
              : []);
        return drawn.map(({ bbox: effectiveBbox, usingAnchor, colorIndex }, anchorIndex) => (
          <ChunkHighlight
            key={`${chunk.chunk_id}:${anchorIndex}`}
            bbox={effectiveBbox}
            colorIndex={colorIndex}
            // The coarse chunk rectangle describes a paragraph, so a full-width
            // band still reads better there. A sentence rect is already trimmed
            // to the cited words and must keep its own x.
            fullWidth={!usingAnchor}
            // ChunkHighlight normalises PDF-point bboxes against
            // the source PDF page dimensions — NOT the rasterised
            // image's pixel size (``page.width`` / ``page.height``,
            // 1275×1650 at 150 DPI).  Using the pixel size here
            // would place highlights at roughly half their correct
            // distance from the top-left corner.  When the bbox
            // came from ``computeAnchorBbox`` it's already in
            // normalised (0-1) coords, so we pass nulls and the
            // component's ``looksNormalized`` path kicks in.
            pageWidth={usingAnchor ? null : page.pdf_width_pt ?? null}
            pageHeight={usingAnchor ? null : page.pdf_height_pt ?? null}
            isActive={chunk.chunk_id === activeChunkId}
            // When the word itself is boxed in the text layer, soften the big
            // chunk rectangle to a faint outline so the word stands out.
            subtle={wordMarking}
            chunkId={chunk.chunk_id}
            onClick={() => handleChunkClick(chunk)}
            // Bilingual viewers (jp_pdf / zh_pdf): the citation highlight must
            // let hover fall through to the translate region layer beneath it,
            // so the user can still hover the cited paragraph to translate it.
            pointerThrough={jpHover}
          />
        ));
      })}

      {/* PAGE-KEYED sentence highlight (progressive-read citation path).

          The chunk loop above can only draw where a chunk was fetched, and a
          progressive read owns no chunk — it addresses its evidence as
          (page, character range) and carries the sentence itself. Everything
          needed to box that sentence is already here: this page's word-level
          ``textSpans`` and the sentence text. The chunk was never more than a
          lookup key; it contributes no geometry to the tight box (see
          ``effectiveBbox`` above, where ``chunk.bbox`` is unused once an anchor
          matched).

          Drawn independently of ``chunks`` for the same reason ``sectionBoxes``
          below is: the citation has no chunk to hang off. Unlike that overlay,
          the box here is COMPUTED from text rather than supplied as geometry,
          so it is a located sentence and not an approximation of one.

          Not gated on the bbox flag, which
          licenses the stored chunk rectangle. There is no rectangle to fall
          back to here and there must not be one: an unlocated anchor draws
          nothing. */}
      {(pageAnchors?.length ?? 0) > 0 && (() => {
        // ONE BOX PER ANCHOR, resolved independently.
        //
        // Per-anchor refusal: a span that cannot be located on this page is a
        // non-event for itself alone. Suppressing a located sibling would turn
        // one missing box into two.
        //
        // Dedupe on the RESOLVED rectangle rather than the anchor text, exactly
        // as the chunk loop above does: two different probes can land on the
        // same words, and stacked overlays double the fill opacity into a
        // colour nobody chose.
        const boxes: { bbox: ChunkBbox; colorIndex: number }[] = [];
        const keptRects: ChunkBbox[] = [];
        for (const [anchorIdx, anchor] of (pageAnchors ?? []).entries()) {
          // Per LINE, same as the chunk path: a wrapped sentence paints a
          // stroke on each line it occupies rather than a rectangle around all
          // of them, and every stroke of one sentence shares its colour.
          for (const box of computeAnchorRects(textSpans, anchor.start, anchor.end, anchor.text)) {
            const before = keptRects.length;
            if (!keepDistinctRect(keptRects, box)) continue;
            if (keptRects.length === before) {
              const idx = boxes.findIndex((t) => !keptRects.includes(t.bbox));
              if (idx >= 0) boxes.splice(idx, 1);
            }
            boxes.push({ bbox: box, colorIndex: anchorIdx });
          }
        }
        return boxes.map(({ bbox, colorIndex }, anchorIndex) => (
          <ChunkHighlight
            key={`page-anchor:${anchorIndex}`}
            bbox={bbox}
            colorIndex={colorIndex}
            // Already normalised 0-1 by computeAnchorBbox, so opt out of
            // ChunkHighlight's PDF-point conversion exactly as the anchored
            // chunk path does. Belt-and-braces: ChunkHighlight checks
            // `looksNormalized` BEFORE `canNormalizeFromPage`, so a 0-1 box is
            // never divided even if these were populated (mutation-verified as
            // an equivalent mutant). Passing nulls states the intent at the
            // call site rather than relying on that precedence holding.
            pageWidth={null}
            pageHeight={null}
            isActive={false}
            // No chunk to select: this is a record of what was cited, not a
            // handle on a retrievable unit. Visual only, and hover falls
            // through to any layer beneath.
            chunkId={null}
            pointerThrough
          />
        ));
      })()}

      {/* Deterministic section-highlight boxes (EU-historical citation path).
          Normalized 0..1 [x0,y0,x1,y1] → percentage-positioned rectangles, the
          same overlay style as a chunk highlight but driven by section geometry
          (no chunk_ids, and word-level text matching can't hit a section phrase). */}
      {(sectionBoxes ?? []).map(([x0, y0, x1, y1], i) => (
        <div
          key={`sec-${i}`}
          className="absolute rounded-sm pointer-events-none"
          style={{
            left: `${x0 * 100}%`,
            top: `${y0 * 100}%`,
            width: `${(x1 - x0) * 100}%`,
            height: `${(y1 - y0) * 100}%`,
            background: 'rgba(250, 204, 21, 0.22)',
            border: '1.5px solid rgba(202, 138, 4, 0.85)',
            zIndex: 14,
          }}
        />
      ))}

      {/* Matched section heading: three blinks, then a calm outline that stays
          until the next jump, so the section start remains findable after the
          animation ends. */}
      {headingBox && (
        <div
          key={`hl-${headingFlashKey ?? 0}`}
          className="absolute rounded-sm pointer-events-none ld-heading-flash"
          style={{
            left: `${headingBox[0] * 100}%`,
            top: `${headingBox[1] * 100}%`,
            width: `${(headingBox[2] - headingBox[0]) * 100}%`,
            height: `${(headingBox[3] - headingBox[1]) * 100}%`,
            zIndex: 15,
          }}
        />
      )}

      {/* Page number badge */}
      <div className="absolute bottom-1 right-2 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
        {pageNum}
      </div>

      {/* Zoom affordance — a magnifier button (the text overlay covers most
          direct image clicks, so this gives a reliable click target). zIndex
          above the text layer so it's always clickable. */}
      {onZoomClick && (
        <button
          type="button"
          onClick={() => onZoomClick(pageNum)}
          title="Zoom into this page"
          aria-label={`Zoom into page ${pageNum}`}
          className="absolute top-1 left-2 flex items-center justify-center rounded bg-black/45 px-1.5 py-1 text-white hover:bg-black/70"
          style={{ zIndex: 30 }}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16zM11 8v6M8 11h6" />
          </svg>
        </button>
      )}

      {/* OCR indicator */}
      {page.is_ocr && (
        <div className="absolute top-1 right-2 bg-orange-500/80 text-white text-xs px-1.5 py-0.5 rounded">
          OCR
        </div>
      )}
    </div>
  );
};

export default React.memo(PageRenderer);
